
import { format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { WeeklyMenu } from '@/types';
import { v4 as uuidv4 } from 'uuid';

// Helper to create a new weekly menu
export const createNewMenu = (userId: string, nutritionGoals?: any): WeeklyMenu => {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const week = format(weekStart, 'yyyy-MM-dd', { locale: es });
  
  if (!nutritionGoals) {
    nutritionGoals = {
      calories: 2000,
      protein: 75,
      carbs: 250,
      fat: 65,
      iron: 18,
      calcium: 1000,
      zinc: 11,
      magnesium: 400,
      omega3: 1.6
    };
  }
  
  // ENFORCE MINIMUM 1500 CALORIES
  if (nutritionGoals.calories < 1500) {
    console.log(`🔒 MENU CREATION: Enforcing minimum 1500 calories (was ${nutritionGoals.calories})`);
    nutritionGoals.calories = 1500;
    
    // Recalculate macros for 1500 calories to maintain proper ratios
    nutritionGoals.fat = Math.round((1500 * 0.25) / 9);
    nutritionGoals.carbs = Math.round((1500 - (nutritionGoals.protein * 4) - (nutritionGoals.fat * 9)) / 4);
  }
  
  const newMenuId = `${userId}_${week}`;
  const newMenu: WeeklyMenu = {
    id: newMenuId,
    userId: userId,
    week: week,
    days: {
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {},
      saturday: {},
      sunday: {}
    },
    nutritionGoals: nutritionGoals,
    created_at: new Date().toISOString()
  };
  return newMenu;
};

// Get the current week string
export const getCurrentWeek = (): string => {
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  return format(weekStart, 'yyyy-MM-dd', { locale: es });
};

// Calculate protein from weight
export const calculateProteinFromWeight = (weight: number): number => {
  const validWeight = isNaN(weight) ? 70 : weight;
  return Math.round(validWeight * 0.8);
};

// Get meal types based on meals per day
export const getMealTypes = (mealsPerDay: number = 3): string[] => {
  const allMealTypes = [
    'breakfast',
    'morning_snack',
    'lunch',
    'afternoon_snack',
    'dinner',
    'evening_snack'
  ];
  
  switch(mealsPerDay) {
    case 3:
      return ['breakfast', 'lunch', 'dinner'];
    case 4:
      return ['breakfast', 'lunch', 'afternoon_snack', 'dinner'];
    case 5:
      return ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'dinner'];
    case 6:
      return allMealTypes;
    default:
      return ['breakfast', 'lunch', 'dinner'];
  }
};

// Check if two objects are deeply equal
export const isEqual = (obj1: any, obj2: any): boolean => {
  if (!obj1 || !obj2) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];
    const areObjects = typeof val1 === 'object' && typeof val2 === 'object';
    
    if (
      (areObjects && !isEqual(val1, val2)) ||
      (!areObjects && Math.abs(Number(val1) - Number(val2)) > 0.1)
    ) {
      return false;
    }
  }
  
  return true;
};

// Calculate meal calorie suggestion based on meal type and total calories
export const getMealCalorieSuggestion = (mealType: string, totalCalories: number, availableMealTypes: string[]): number => {
  // ENFORCE MINIMUM 1500 CALORIES BEFORE CALCULATING MEAL DISTRIBUTION
  const enforcedTotalCalories = Math.max(1500, totalCalories);
  
  if (enforcedTotalCalories !== totalCalories) {
    console.log(`🔒 MEAL CALCULATION: Enforcing minimum 1500 calories for meal distribution (was ${totalCalories})`);
  }
  
  // Calculate distribution based on available meal types
  const mealTypeCount = availableMealTypes.length;
  if (mealTypeCount <= 0) return 0;
  
  // Different calorie distribution based on number of meals
  switch (mealTypeCount) {
    case 3: // Standard 3 meals
      switch (mealType) {
        case 'breakfast':
          return Math.round(enforcedTotalCalories * 0.3);
        case 'lunch':
          return Math.round(enforcedTotalCalories * 0.35);
        case 'dinner':
          return Math.round(enforcedTotalCalories * 0.35);
        default:
          return 0;
      }
    
    case 4: // 3 meals + 1 snack
      switch (mealType) {
        case 'breakfast':
          return Math.round(enforcedTotalCalories * 0.25);
        case 'lunch':
          return Math.round(enforcedTotalCalories * 0.35);
        case 'afternoon_snack':
          return Math.round(enforcedTotalCalories * 0.1);
        case 'dinner':
          return Math.round(enforcedTotalCalories * 0.3);
        default:
          return 0;
      }
    
    case 5: // 3 meals + 2 snacks
      switch (mealType) {
        case 'breakfast':
          return Math.round(enforcedTotalCalories * 0.25);
        case 'morning_snack':
          return Math.round(enforcedTotalCalories * 0.1);  
        case 'lunch':
          return Math.round(enforcedTotalCalories * 0.3);
        case 'afternoon_snack':
          return Math.round(enforcedTotalCalories * 0.1);
        case 'dinner':
          return Math.round(enforcedTotalCalories * 0.25);
        default:
          return 0;
      }
    
    case 6: // 3 meals + 3 snacks
      switch (mealType) {
        case 'breakfast':
          return Math.round(enforcedTotalCalories * 0.2);
        case 'morning_snack':
          return Math.round(enforcedTotalCalories * 0.1);
        case 'lunch':
          return Math.round(enforcedTotalCalories * 0.25);
        case 'afternoon_snack':
          return Math.round(enforcedTotalCalories * 0.1);
        case 'dinner':
          return Math.round(enforcedTotalCalories * 0.25);
        case 'evening_snack':
          return Math.round(enforcedTotalCalories * 0.1);
        default:
          return 0;
      }
    
    default:
      // Fallback: distribute evenly
      return Math.round(enforcedTotalCalories / mealTypeCount);
  }
};
